import React, { useMemo, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { useShallow } from "zustand/react/shallow";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { resolveDatasetColor } from "@/lib/datasetColor";
import { isAbandoned } from "@/lib/api/organizations";
import {
  flattenSpaceTree,
  spaceColor,
  visibleSpaces,
  useOrgContextStore,
  type SpacePath,
} from "@/components/main/stores/orgContextStore";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { SpaceCreateDialog } from "./SpaceCreateDialog";

interface SpacesRailProps {
  /// Rail filter: clicking a space narrows the overview to it; clicking the
  /// active one there clears the filter. From any other page a click goes to
  /// the overview with that space selected.
  selectedSpaceId: string | null;
  onSelectSpace: (spaceId: string | null) => void;
}

/// The sidebar SPACES section (S6d): collapsible tree with persisted collapse
/// state, per-space model counts, and the lock on sealed spaces. Space
/// creation is never a quick action — the header "+" opens the dialog for a
/// new root, a row's hover "+" for a child inside that space (D50: the entry
/// point decides where, the dialog never asks).
export const SpacesRail: React.FC<SpacesRailProps> = ({
  selectedSpaceId,
  onSelectSpace,
}) => {
  // null = founding a root; undefined = closed.
  const [createParent, setCreateParent] = useState<string | null | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  // The row of the space whose settings are open stays highlighted.
  const settingsSpaceId =
    useMatch("/spaces/:space_id")?.params.space_id ?? null;
  const onOverview = useMatch("/overview") !== null;
  const { spaces, collapsed, toggleCollapsed, spacesLoading } =
    useOrgContextStore(
      useShallow((s) => ({
        spaces: s.spaces,
        collapsed: s.collapsed,
        toggleCollapsed: s.toggleCollapsed,
        spacesLoading: s.spacesLoading,
      })),
    );
  const paths = useMemo(
    () => flattenSpaceTree(visibleSpaces(spaces)),
    [spaces],
  );
  const models = useMainStateStore((s) => s.availableModels);

  const countBySpace = useMemo(() => {
    const counts = new Map<string, number>();
    for (const model of models) {
      const spaceId = model.space_id;
      if (spaceId) counts.set(spaceId, (counts.get(spaceId) ?? 0) + 1);
    }
    return counts;
  }, [models]);

  // From the DRAWN set: a doomed child is in the Trash, and a chevron on its
  // parent would expand to nothing.
  const hasChildren = useMemo(() => {
    const parents = new Set<string>();
    for (const { space } of paths) {
      if (space.parent_space_id) parents.add(space.parent_space_id);
    }
    return parents;
  }, [paths]);

  // A collapsed ancestor hides its whole subtree.
  const visible = useMemo(() => {
    const byId = new Map(spaces.map((s) => [s.space_id, s]));
    return paths.filter(({ space }) => {
      let parent = space.parent_space_id;
      while (parent) {
        if (collapsed[parent]) return false;
        parent = byId.get(parent)?.parent_space_id ?? null;
      }
      return true;
    });
  }, [paths, spaces, collapsed]);

  return (
    <div className="flex flex-col">
      <div className="mb-1 flex items-center justify-between pl-2">
        <span
          className="font-mono text-fg-muted uppercase select-none"
          style={{
            fontSize: "var(--font-size-x-small)",
            letterSpacing: "0.1em",
          }}
        >
          Spaces
        </span>
        <span className="flex items-center">
          <TooltipButton
            title="New space"
            aria-label="New space"
            variant="contextual"
            size="xs"
            onClick={() => setCreateParent(null)}
          >
            <AddIcon fontSize="inherit" />
          </TooltipButton>
        </span>
      </div>

      {spacesLoading && spaces.length === 0 ? (
        <span className="px-2 text-fg-muted-regular-small">Loading…</span>
      ) : (
        visible.map((path) => {
          const spaceId = path.space.space_id;
          const openSettings = () => navigate(`/spaces/${spaceId}`);
          return (
            <SpaceRow
              key={spaceId}
              path={path}
              count={countBySpace.get(spaceId) ?? 0}
              expandable={hasChildren.has(spaceId)}
              isCollapsed={!!collapsed[spaceId]}
              isSelected={(settingsSpaceId ?? selectedSpaceId) === spaceId}
              onToggleCollapse={() => toggleCollapsed(spaceId)}
              onOpenSettings={openSettings}
              onCreateInside={() => setCreateParent(spaceId)}
              onSelect={() => {
                if (onOverview) {
                  onSelectSpace(selectedSpaceId === spaceId ? null : spaceId);
                  return;
                }
                onSelectSpace(spaceId);
                navigate("/overview");
              }}
            />
          );
        })
      )}

      <SpaceCreateDialog
        open={createParent !== undefined}
        onClose={() => setCreateParent(undefined)}
        parentId={createParent ?? null}
      />
    </div>
  );
};

const SpaceRow: React.FC<{
  path: SpacePath;
  count: number;
  expandable: boolean;
  isCollapsed: boolean;
  isSelected: boolean;
  onToggleCollapse: () => void;
  onOpenSettings: () => void;
  onCreateInside: () => void;
  onSelect: () => void;
}> = ({
  path,
  count,
  expandable,
  isCollapsed,
  isSelected,
  onToggleCollapse,
  onOpenSettings,
  onCreateInside,
  onSelect,
}) => {
  const { space, ancestors } = path;
  const Chevron = isCollapsed ? ChevronRightIcon : ExpandMoreIcon;

  return (
    <div
      className={`group flex h-7 items-center gap-1.5 rounded-app pr-1 ${
        isSelected ? "bg-list-selected-light" : "hover:bg-list-hover"
      }`}
      style={{
        paddingLeft: `calc(var(--spacing-sm) + ${ancestors.length} * var(--spacing-md))`,
      }}
    >
      {expandable ? (
        <button
          aria-label={isCollapsed ? "Expand" : "Collapse"}
          onClick={onToggleCollapse}
          className="flex h-3 w-3 shrink-0 items-center justify-center text-fg-muted opacity-65 hover:opacity-100"
        >
          <Chevron sx={{ fontSize: 12 }} />
        </button>
      ) : (
        <span className="h-3 w-3 shrink-0" />
      )}
      <button
        onClick={onSelect}
        className="flex h-full min-w-0 flex-1 items-center gap-1.5 text-left"
      >
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-app"
          style={{
            backgroundColor: resolveDatasetColor(spaceColor(space)),
          }}
        />
        <span
          className={`min-w-0 flex-1 truncate ${
            isSelected
              ? "text-fg-regular"
              : "text-fg-muted-regular group-hover:text-[var(--color-fg)]"
          }`}
        >
          {space.name}
        </span>
        {space.sealed && (
          <Tooltip
            title={"Sealed space\nInvisible without a grant · nothing leaves"}
          >
            <LockOutlinedIcon
              sx={{ fontSize: 11 }}
              className="shrink-0 text-fg-muted"
            />
          </Tooltip>
        )}
        {/* Only an org governor is served the count, so only they see this —
            an abandoned space has no manager left to show it to. */}
        {isAbandoned(space) && (
          <Tooltip title={"No manager\nOpen the space settings to assign one"}>
            <PersonOffOutlinedIcon
              sx={{ fontSize: 11 }}
              className="shrink-0 text-warning"
              aria-label="No manager"
            />
          </Tooltip>
        )}
      </button>
      <span className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100">
        <TooltipButton
          title={`New space inside ${space.name}`}
          aria-label={`New space inside ${space.name}`}
          variant="contextual"
          size="xs"
          onClick={onCreateInside}
        >
          <AddIcon fontSize="inherit" />
        </TooltipButton>
        <TooltipButton
          title="Space settings"
          aria-label={`Settings for ${space.name}`}
          variant="contextual"
          size="xs"
          onClick={onOpenSettings}
        >
          <SettingsOutlinedIcon fontSize="inherit" />
        </TooltipButton>
      </span>
      {count > 0 && (
        <span
          className="shrink-0 font-mono text-fg-muted opacity-70"
          style={{
            fontSize: "var(--font-size-x-small)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
};
